/**
 * Admin Communications — send a "log your minutes" reminder or a challenge
 * update / newsletter to Everyone, the KMS team, or a custom set of people.
 * Preview the recipient list and the email itself before sending, and keep
 * a running send history so nobody gets over-emailed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Megaphone, EnvelopeSimple, PaperPlaneTilt, Eye, Users, Check,
} from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const num = (n) => Number(n || 0).toLocaleString();

const AUDIENCES = [
  ["everyone", "Everyone"],
  ["kms", "KMS Team"],
  ["custom", "Custom"],
];

const Field = ({ label, value, onChange, testId, placeholder }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-1 w-full bg-black border border-white/15 focus:border-[#FF1B91] outline-none px-3 py-2 text-sm text-white"
    />
  </label>
);

const Area = ({ label, value, onChange, testId, rows = 5, hint }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">{label}</span>
    {hint && <span className="text-white/35 text-[10px] ml-2">{hint}</span>}
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      data-testid={testId}
      className="mt-1 w-full bg-black border border-white/15 focus:border-[#FF1B91] outline-none px-3 py-2 text-sm text-white leading-relaxed"
    />
  </label>
);

export default function CommunicationsPanel({ token }) {
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [tool, setTool] = useState("reminder");
  const [audience, setAudience] = useState("everyone");
  const [customIds, setCustomIds] = useState([]);
  const [recips, setRecips] = useState(null);
  const [ctx, setCtx] = useState(null);

  // Reminder fields
  const [rSubject, setRSubject] = useState("");
  const [rBody, setRBody] = useState("");

  // Newsletter fields
  const [nSubject, setNSubject] = useState("");
  const [nHeadline, setNHeadline] = useState("");
  const [nBody, setNBody] = useState("");
  const [nNews, setNNews] = useState("");
  const [nCta, setNCta] = useState("");
  const [incStats, setIncStats] = useState(true);
  const [incFinishers, setIncFinishers] = useState(true);
  const [incStandings, setIncStandings] = useState(true);
  const [incMilestones, setIncMilestones] = useState(true);
  const [depth, setDepth] = useState(10);

  const [testOnly, setTestOnly] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [history, setHistory] = useState([]);

  // Load default templates + context + history once.
  const loadDefaults = useCallback(async () => {
    try {
      const [rd, nd, c, h] = await Promise.all([
        fetch(`${BACKEND}/api/admin/comms/reminder/default`, { headers }).then((r) => r.json()),
        fetch(`${BACKEND}/api/admin/comms/newsletter/default`, { headers }).then((r) => r.json()),
        fetch(`${BACKEND}/api/admin/comms/context?standings_depth=${depth}`, { headers }).then((r) => r.json()),
        fetch(`${BACKEND}/api/admin/comms/history`, { headers }).then((r) => r.json()),
      ]);
      setRSubject((v) => v || rd.subject || "");
      setRBody((v) => v || rd.body || "");
      setNSubject((v) => v || nd.subject || "");
      setNHeadline((v) => v || nd.headline || "");
      setNBody((v) => v || nd.body || "");
      setNCta((v) => v || nd.cta_text || "");
      setCtx(c);
      setHistory(h.history || []);
    } catch (e) {
      setMsg(e?.message || "Failed to load templates");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadDefaults(); }, [loadDefaults]);

  // Load recipients when the audience or custom picks change.
  const loadRecipients = useCallback(async () => {
    try {
      const ids = audience === "custom" ? `&ids=${customIds.join(",")}` : "";
      const r = await fetch(`${BACKEND}/api/admin/comms/recipients?audience=${audience}${ids}`, { headers });
      setRecips(await r.json());
    } catch (e) {
      setRecips({ error: e?.message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, customIds, token]);

  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const buildPayload = () => (tool === "reminder"
    ? { type: "reminder", audience, custom_ids: customIds, subject: rSubject, body: rBody,
        standings_depth: depth, test_only: testOnly }
    : { type: "newsletter", audience, custom_ids: customIds, subject: nSubject,
        headline: nHeadline, body: nBody, news_text: nNews, cta_text: nCta,
        include_stats: incStats, include_finishers: incFinishers,
        include_standings: incStandings, include_milestones: incMilestones,
        standings_depth: depth, test_only: testOnly });

  const openPreview = async () => {
    setMsg("Rendering preview…");
    try {
      const r = await fetch(`${BACKEND}/api/admin/comms/preview`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Preview failed");
      const w = window.open("", "_blank");
      if (w) { w.document.write(j.html); w.document.close(); setMsg(""); }
      else setMsg("Allow pop-ups to see the preview.");
    } catch (e) {
      setMsg(e?.message || "Preview failed");
    }
  };

  const send = async () => {
    const count = recips?.will_receive || 0;
    const who = testOnly ? "only you (test)" : `${count} recipient${count === 1 ? "" : "s"}`;
    if (!window.confirm(`Send this ${tool} to ${who}?`)) return;
    setSending(true); setMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/comms/send`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Send failed");
      setMsg(`Sent to ${j.recipient_count} · ${j.failed} failed${j.skipped_no_email ? ` · ${j.skipped_no_email} skipped (no email)` : ""}`);
      const h = await fetch(`${BACKEND}/api/admin/comms/history`, { headers }).then((x) => x.json());
      setHistory(h.history || []);
    } catch (e) {
      setMsg(e?.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const toggleCustom = (id) => {
    setCustomIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const allPeople = recips?.all || [];

  return (
    <CollapsibleSection
      id="communications"
      testId="communications-panel"
      title="Communications"
      icon={<Megaphone size={20} weight="bold" className="text-[#FF1B91]" />}
      subtitle="Send a log-your-minutes reminder or a challenge update to Everyone, KMS, or a custom list."
      onRefresh={async () => { await loadDefaults(); await loadRecipients(); }}
      defaultOpen
    >
      <div className="max-w-4xl space-y-6">
        {/* Tool switch */}
        <div className="flex flex-wrap gap-2">
          {[["reminder", "Send Reminder"], ["newsletter", "Send Challenge Update"]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              data-testid={`comms-tool-${k}`}
              onClick={() => { setTool(k); setMsg(""); }}
              className={`text-[11px] uppercase tracking-[0.2em] font-bold px-4 py-2 border transition-colors ${
                tool === k ? "border-[#FF1B91] bg-[#FF1B91]/15 text-white" : "border-white/15 text-white/55 hover:text-white"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Audience selector */}
        <div className="space-y-2">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">Audience</span>
          <div className="flex flex-wrap gap-2">
            {AUDIENCES.map(([k, l]) => (
              <button
                key={k}
                type="button"
                data-testid={`comms-audience-${k}`}
                onClick={() => setAudience(k)}
                className={`text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-2 border ${
                  audience === k ? "border-[#FF1B91] bg-[#FF1B91]/15 text-white" : "border-white/15 text-white/55 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Custom picker */}
        {audience === "custom" && (
          <div data-testid="comms-custom-picker" className="border border-white/10 max-h-56 overflow-y-auto p-2 space-y-1">
            {allPeople.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-[12px] text-white/80 px-2 py-1 hover:bg-white/[0.03] cursor-pointer">
                <input
                  type="checkbox"
                  checked={customIds.includes(p.id)}
                  onChange={() => toggleCustom(p.id)}
                  data-testid={`comms-custom-${p.id}`}
                />
                <span className="flex-1">{p.name || "no name"} {p.kms_team && <span className="text-[#FF1B91] text-[9px] uppercase">KMS</span>}</span>
                <span className="text-white/40">{num(p.minutes)}m · {p.percent}%</span>
                {!p.has_email && <span className="text-yellow-400/70 text-[9px]">no email</span>}
              </label>
            ))}
            {allPeople.length === 0 && <p className="text-white/40 text-xs p-2">No participants.</p>}
          </div>
        )}

        {/* Recipient preview */}
        {recips && !recips.error && (
          <div data-testid="comms-recipient-preview" className="border border-white/10 bg-black/40 px-4 py-3 flex flex-wrap items-center gap-4">
            <Users size={16} weight="bold" className="text-[#FF1B91]" />
            <span className="text-sm text-white">
              <strong data-testid="comms-will-receive">{num(recips.will_receive)}</strong> will receive this
            </span>
            <span className="text-white/50 text-[12px]">{num(recips.total_selected)} selected</span>
            {recips.skipped_no_email > 0 && (
              <span className="text-yellow-400/80 text-[12px]" data-testid="comms-skipped">
                {num(recips.skipped_no_email)} skipped (no email — text them instead)
              </span>
            )}
          </div>
        )}

        {/* Editor */}
        {tool === "reminder" ? (
          <div className="space-y-4">
            <Field label="Subject" value={rSubject} onChange={setRSubject} testId="comms-reminder-subject" />
            <Area
              label="Body" value={rBody} onChange={setRBody} testId="comms-reminder-body" rows={7}
              hint="Tokens: {first_name} {minutes} {percent} {remaining}"
            />
            <p className="text-white/40 text-[11px]">
              Each person also gets their own progress bar, the current standings, and a Log-my-minutes button automatically.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <Field label="Subject" value={nSubject} onChange={setNSubject} testId="comms-news-subject" />
            <Field label="Headline" value={nHeadline} onChange={setNHeadline} testId="comms-news-headline" />
            <Area label="Update / intro" value={nBody} onChange={setNBody} testId="comms-news-body" rows={4} />
            <div className="flex flex-wrap gap-4">
              {[["Stats", incStats, setIncStats, "stats"], ["Finisher shoutouts", incFinishers, setIncFinishers, "finishers"],
                ["Standings", incStandings, setIncStandings, "standings"], ["Milestones", incMilestones, setIncMilestones, "milestones"]].map(
                ([l, val, set, k]) => (
                  <label key={k} className="inline-flex items-center gap-2 text-[12px] text-white/80">
                    <input type="checkbox" checked={val} onChange={() => set(!val)} data-testid={`comms-include-${k}`} />
                    {l}
                  </label>
                ))}
            </div>
            <Area label="News / media (optional)" value={nNews} onChange={setNNews} testId="comms-news-media" rows={3} />
            <Area label="Call to action" value={nCta} onChange={setNCta} testId="comms-news-cta" rows={3} />
          </div>
        )}

        {/* Standings depth */}
        <label className="flex items-center gap-3 text-[12px] text-white/70">
          Standings depth
          <input
            type="number" min="1" max="200" value={depth}
            onChange={(e) => setDepth(parseInt(e.target.value || "10", 10))}
            data-testid="comms-standings-depth"
            className="w-20 bg-black border border-white/15 focus:border-[#FF1B91] outline-none px-2 py-1 text-white"
          />
          <span className="text-white/40">top participants shown in the email</span>
        </label>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
          <label className="inline-flex items-center gap-2 text-[12px] text-white/80 mr-2" data-testid="comms-test-toggle">
            <input type="checkbox" checked={testOnly} onChange={() => setTestOnly(!testOnly)} data-testid="comms-test-only" />
            Send only to me (test)
          </label>
          <button
            type="button" onClick={openPreview} data-testid="comms-preview-btn"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/25 text-white hover:border-[#FF1B91] px-4 py-2"
          >
            <Eye size={12} weight="bold" /> Preview
          </button>
          <button
            type="button" onClick={send} disabled={sending} data-testid="comms-send-btn"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/25 disabled:opacity-40 px-4 py-2"
          >
            <PaperPlaneTilt size={12} weight="bold" /> {sending ? "Sending…" : testOnly ? "Send test to me" : "Send"}
          </button>
          {msg && <span className="text-[11px] text-emerald-300" data-testid="comms-msg">{msg}</span>}
        </div>

        {/* History */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 mb-2 flex items-center gap-2">
            <EnvelopeSimple size={13} weight="bold" /> Send history
          </div>
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="comms-history-table">
              <thead className="bg-[#0F0F0F]">
                <tr className="border-b border-white/10 text-white/40 uppercase tracking-[0.15em] text-[9px]">
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Audience</th>
                  <th className="p-2 text-right">Recipients</th>
                  <th className="p-2 text-left">Subject</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-white/5" data-testid={`comms-history-row-${h.id}`}>
                    <td className="p-2 text-white/60 whitespace-nowrap">{new Date(h.sent_at).toLocaleString()}</td>
                    <td className="p-2 text-white/80 capitalize">{h.type}{h.test_only ? " (test)" : ""}</td>
                    <td className="p-2 text-white/60 capitalize">{h.audience}</td>
                    <td className="p-2 text-right text-white font-bold tabular-nums">{num(h.recipient_count)}</td>
                    <td className="p-2 text-white/70 truncate max-w-[240px]">{h.subject}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan={5} className="p-3 text-center text-white/40">Nothing sent yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
}
